import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import TrainingPage from './TrainingPage';
import SimulationTrainingPage from './SimulationTrainingPage';
import { useLanguage } from './contexts/LanguageContext';
import LanguageDropdown from './components/LanguageDropdown';
import './HomePage.css';

// 添加一个测试注释，检查文件是否更新
// TEST UPDATE - 2023-04-05-1

const HomePage = ({ onStartSinglePlayer, onStartMultiPlayer }) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  // 状态管理
  const [username, setUsername] = useState('');
  const [deadline, setDeadline] = useState('');
  const [showTrainingPage, setShowTrainingPage] = useState(false);
  const [trainingMode, setTrainingMode] = useState(''); // 'simulation' 或 'mockTest'
  // 添加开发中提示框状态
  const [showDevelopingModal, setShowDevelopingModal] = useState(false);
  // 添加登录状态
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  
  // 雅思许愿池状态
  const [wishPoolStep, setWishPoolStep] = useState(1);
  const [idealScore, setIdealScore] = useState('');
  const [showOptimizationModal, setShowOptimizationModal] = useState(false);

  // 功能更新通知
  const [showUpdateNotice, setShowUpdateNotice] = useState(() => {
    return localStorage.getItem('talktastic_update_v3_dismissed') !== 'true';
  });

  // 检查用户登录状态
  useEffect(() => {
    const storedUser = localStorage.getItem('talktastic_user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        // 检查用户是否已登录，需要同时满足isRegistered和isLoggedIn为true
        if (user && user.isRegistered && user.isLoggedIn) {
          setIsLoggedIn(true);
          setUserData(user);
        } else {
          // 用户未登录或已退出
          setIsLoggedIn(false);
          setUserData(null);
        }
      } catch (error) {
        console.error('读取用户信息失败:', error);
        setIsLoggedIn(false);
        setUserData(null);
      }
    } else {
      setIsLoggedIn(false);
      setUserData(null);
    }
  }, []);

  // 跳转到账号中心
  const handleAccountCenterClick = () => {
    navigate('/account');
  };

  // 加载本地存储的值
  useEffect(() => {
    const savedUsername = localStorage.getItem('talktastic_username');
    const savedDeadline = localStorage.getItem('talktastic_deadline');
    
    if (savedUsername) {
      setUsername(savedUsername);
    }
    
    if (savedDeadline) {
      setDeadline(savedDeadline);
    }
  }, []);

  // 处理表单输入
  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    localStorage.setItem('talktastic_username', value);
  };

  const handleDeadlineChange = (e) => {
    const value = e.target.value;
    setDeadline(value);
    localStorage.setItem('talktastic_deadline', value);
  };

  // 处理开始分项训练按钮点击
  const handleStartTraining = () => {
    // 直接跳转到模拟测试页面，不再显示下拉框和截止日期选择
    setTrainingMode('simulation');
    setShowTrainingPage(true);
  };

  // 处理理想分数变更
  const handleIdealScoreChange = (e) => {
    const value = e.target.value;
    if (/^$|^[0-9]+(\.[0-9]?)?$/.test(value) && parseFloat(value || 0) <= 9) {
      setIdealScore(value);
    }
  };
  
  // 处理雅思许愿池按钮点击
  const handleWishPoolButtonClick = () => {
    setWishPoolStep(2);
  };
  
  // 处理返回按钮点击
  const handleWishPoolBack = () => {
    setWishPoolStep(1);
    setIdealScore('');
  };

  // 关闭优化模态框
  const handleCloseOptimizationModal = () => {
    setShowOptimizationModal(false);
  };

  // 显示训练页面
  if (showTrainingPage) {
    if (trainingMode === 'mockTest') {
      return (
        <TrainingPage 
          deadline={deadline}
          username={username}
          mode={trainingMode}
        />
      );
    } else if (trainingMode === 'simulation') {
      return (
        <SimulationTrainingPage 
          deadline={deadline}
          username={username}
          purpose="Simulation Training"
          onBack={() => setShowTrainingPage(false)}
        />
      );
    }
  }

  // 处理关闭开发中提示框
  const handleCloseDevelopingModal = () => {
    setShowDevelopingModal(false);
  };

  const handleDismissUpdate = () => {
    setShowUpdateNotice(false);
    localStorage.setItem('talktastic_update_v3_dismissed', 'true');
  };

  const handleGoToFreeTalk = () => {
    setShowUpdateNotice(false);
    localStorage.setItem('talktastic_update_v3_dismissed', 'true');
    setTrainingMode('simulation');
    setShowTrainingPage(true);
  };

  return (
    <div className="homepage-container">
      {/* 添加导航栏 */}
      <nav className="main-nav">
        <div className="nav-container">
          <div className="navbar-links">
            {isLoggedIn && (
              <div 
                className="account-center-icon navbar-account-icon" 
                title={language === 'en' ? 'Account Center' : '账号中心'}
                onClick={handleAccountCenterClick}
                style={{ marginRight: '15px' }}
              >
                <i className="fas fa-user"></i>
              </div>
            )}
            <a href="#" className="nav-item" onClick={() => {
              setTrainingMode('simulation');
              setShowTrainingPage(true);
            }}>
              {t('simulation_training')}
            </a>
            <a href="#" className="nav-item" onClick={() => {
              setTrainingMode('mockTest');
              setShowTrainingPage(true);
            }}>
              {t('mock_exam')}
            </a>
            <a href="#" className="nav-item" onClick={() => {
              navigate('/knowledge');
            }}>
              {t('knowledge_base')}
            </a>
            <a href="#" className="nav-item" onClick={() => navigate('/profile')}>
              {t('personal_profile')}
            </a>
          </div>
          <div className="nav-right">
            <LanguageDropdown />
          </div>
        </div>
      </nav>
      
      {/* 添加固定在左侧的雅思许愿池卡片 */}
      <div className="fixed-wish-pool-card">
        <div className="topic-image">
          <img src="/images/topic1.png" alt={t('wish_pool')} className="topic-img" />
        </div>
        <div className="topic-content">
          {wishPoolStep === 1 ? (
            <button 
              className="wish-pool-button wishpool-main-button"
              onClick={handleWishPoolButtonClick}
            >
              {t('wish_pool')}
            </button>
          ) : (
            <div className="wish-pool-card-content-inline">
              {wishPoolStep === 2 ? (
                <>
                  <div className="wish-pool-card-score-input">
                    <label htmlFor="ideal-score">{t('your_ideal_score')}</label>
                    <input
                      type="text"
                      id="ideal-score"
                      placeholder={t('score_placeholder')}
                      value={idealScore}
                      onChange={handleIdealScoreChange}
                      className="wish-pool-card-input"
                    />
                  </div>
                  
                  {idealScore && (
                    <div className="wish-pool-card-lucky centered">
                      <div className="clover-icon">🍀</div>
                      <div className="lucky-text-container">
                        <p className="wish-pool-card-message">{t('luck_boosting')}<span className="loading-dots"></span></p>
                        <div className="plus-one-animation">+1</div>
                      </div>
                    </div>
                  )}
                  <button 
                    className="wish-pool-button wishpool-back-button"
                    onClick={handleWishPoolBack}
                  >
                    {t('back_topic')}
                  </button>
                </>
              ) : (
                <>
                  <p className="wish-pool-card-description">{t('your_score')} {idealScore}</p>
                  <div className="wish-pool-card-lucky centered">
                    <div className="clover-icon">🍀</div>
                    <div className="lucky-text-container">
                      <p className="wish-pool-card-message">{t('luck_boosting')}<span className="loading-dots"></span></p>
                      <div className="plus-one-animation">+1</div>
                    </div>
                  </div>
                  <button 
                    className="wish-pool-button wishpool-back-button"
                    onClick={handleWishPoolBack}
                  >
                    {t('back_topic')}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* 主要内容区域 - 整合原有和新增内容 */}
      <section className="hero-section">
        <div className="section-container hero-container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">{t('hero_title')}</h1>
              <p className="hero-subtitle-middle">{t('hero_subtitle_middle')}</p>
              <p className="hero-subtitle">{t('hero_subtitle')}</p>
            </div>
            
            <div className="hero-topics">
              <div className="floating-topics-container">
                <div className="floating-topic-item">
                  <div className="like-count">
                    <span className="like-icon">❤️</span>
                    <span className="like-number">128</span>
                  </div>
                  <div className="topic-bubble">Part 1: Do you think AI tools like GPT-5 will change how people learn languages?</div>
                </div>
                <div className="floating-topic-item">
                  <div className="like-count">
                    <span className="like-icon">❤️</span>
                    <span className="like-number">89</span>
                  </div>
                  <div className="topic-bubble">Part 3: Should governments invest more in cancer research than infectious diseases like HIV?</div>
                </div>
                <div className="floating-topic-item">
                  <div className="like-count">
                    <span className="like-icon">❤️</span>
                    <span className="like-number">76</span>
                  </div>
                  <div className="topic-bubble">Part 2: Describe a time when you read news about an environmental issue (e.g. nuclear wastewater).</div>
                </div>
                <div className="floating-topic-item">
                  <div className="like-count">
                    <span className="like-icon">❤️</span>
                    <span className="like-number">92</span>
                  </div>
                  <div className="topic-bubble">Part 1: Have you ever used cryptocurrency like Bitcoin? Why or why not?</div>
                </div>
                <div className="floating-topic-item">
                  <div className="like-count">
                    <span className="like-icon">❤️</span>
                    <span className="like-number">85</span>
                  </div>
                  <div className="topic-bubble">Part 3: What are the potential dangers of brain-computer interface technology?</div>
                </div>
                <div className="floating-topic-item">
                  <div className="like-count">
                    <span className="like-icon">❤️</span>
                    <span className="like-number">78</span>
                  </div>
                  <div className="topic-bubble">Part 1: Is water pollution a problem in your hometown?</div>
                </div>
                <div className="floating-topic-item">
                  <div className="like-count">
                    <span className="like-icon">❤️</span>
                    <span className="like-number">95</span>
                  </div>
                  <div className="topic-bubble">Part 2: Describe a piece of technology you think will be important in the future (e.g. Neuralink).</div>
                </div>
                <div className="floating-topic-item">
                  <div className="like-count">
                    <span className="like-icon">❤️</span>
                    <span className="like-number">82</span>
                  </div>
                  <div className="topic-bubble">Part 3: How can individuals contribute to solving global environmental problems?</div>
                </div>
                <div className="floating-topic-item">
                  <div className="like-count">
                    <span className="like-icon">❤️</span>
                    <span className="like-number">88</span>
                  </div>
                  <div className="topic-bubble">Part 1: Would you volunteer for a medical vaccine trial?</div>
                </div>
                <div className="floating-topic-item">
                  <div className="like-count">
                    <span className="like-icon">❤️</span>
                    <span className="like-number">91</span>
                  </div>
                  <div className="topic-bubble">Part 2: Talk about an economic trend you find interesting (e.g. Bitcoin halving).</div>
                </div>
                <div className="floating-topic-item">
                  <div className="like-count">
                    <span className="like-icon">❤️</span>
                    <span className="like-number">84</span>
                  </div>
                  <div className="topic-bubble">Part 3: Should AI developers be legally responsible for misuse of their products?</div>
                </div>
                <div className="floating-topic-item">
                  <div className="like-count">
                    <span className="like-icon">❤️</span>
                    <span className="like-number">77</span>
                  </div>
                  <div className="topic-bubble">Part 1: Do you prefer cash or digital payments?</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* 添加特色内容模块 */}
      <section className="features-section">
        <div className="section-container">
          <h2 className="section-title">{t('speaking_training')}</h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">🎮</div>
              <h3>{t('gamified_mode')}</h3>
              <p>{t('play_and_practice')}</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🌐</div>
              <h3>{t('immersive_scene')}</h3>
              <p>{t('immersive_scene_desc')}</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🔄</div>
              <h3>{t('multiple_coverage')}</h3>
              <p>{t('multiple_coverage_desc')}</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📊</div>
              <h3>{t('precise_assessment')}</h3>
              <p>{t('precise_assessment_desc')}</p>
            </div>
          </div>
          <div className="features-action">
            <button className="feature-main-btn" onClick={handleStartTraining}>{t('start_simulation')}</button>
          </div>
        </div>
      </section>
      
      {/* 添加统计数据模块 */}
      <section className="stats-section">
        <div className="section-container">
          <h2 className="section-title">{t('topic_library')}</h2>
          <div className="stats-grid">
            <div className="stat-item topic-stat-item">
              <div className="topic-icon">🎯</div>
              <h3 className="topic-title">{t('precise_prediction')}</h3>
              <p className="topic-desc">{t('prediction_desc')}</p>
            </div>
            <div className="stat-item topic-stat-item">
              <div className="topic-icon">💡</div>
              <h3 className="topic-title">{t('share_inspiration')}</h3>
              <p className="topic-desc">{t('share_desc')}</p>
            </div>
            <div className="stat-item topic-stat-item">
              <div className="topic-icon">🌍</div>
              <h3 className="topic-title">{t('global_thinking')}</h3>
              <p className="topic-desc">{t('global_desc')}</p>
            </div>
          </div>
          <div className="topic-action">
            <button className="topic-main-btn" onClick={() => navigate('/exploration')}>{t('participate_topic')}</button>
          </div>
        </div>
      </section>
      
      {/* 页脚 */}
      <footer className="main-footer">
        <div className="footer-container">
          <div className="footer-logo">
            <span className="logo-text">Talktastic</span>
            <p className="footer-slogan">{t('improve_english')}</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>{t('quick_links')}</h4>
              <a href="/">{t('home')}</a>
              <a href="/training">{t('training_modules')}</a>
              <a href="/exploration">{t('topic_exploration')}</a>
            </div>
            <div className="footer-column">
              <h4>{t('contact_us')}</h4>
              <a href="mailto:athenazhou71@gmail.com">athenazhou71@gmail.com</a>
            </div>
            <div className="footer-column">
              <h4>{t('follow_us')}</h4>
              <div className="social-links">
                <button className="social-link">{t('facebook')}</button>
                <button className="social-link">{t('twitter')}</button>
                <button className="social-link">{t('instagram')}</button>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>{t('copyright')}</p>
        </div>
      </footer>
      
      {/* Optimization Modal */}
      {showOptimizationModal && (
        <div className="report-modal-overlay">
          <div className="optimization-modal">
            <h3 className="modal-title">{t('mock_exam')}</h3>
            <div className="modal-content">
              <div className="modal-icon">🔧</div>
              <h2>{t('optimizing')}</h2>
              <p>{t('optimization_message')}</p>
            </div>
            <div className="modal-actions">
              <button onClick={handleCloseOptimizationModal} className="primary-btn">
                {t('return_home')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 开发中提示框 */}
      {showDevelopingModal && (
        <div className="report-modal-overlay">
          <div className="optimization-modal">
            <h3 className="modal-title">{t('simulation_training')}</h3>
            <div className="modal-content">
              <div className="modal-icon">🚧</div>
              <h2>{language === 'en' ? 'Under Development' : '功能开发中'}</h2>
              <p>{language === 'en' ? 'This feature is currently under development. Please check back later.' : '该功能正在开发中，请稍后再试。'}</p>
            </div>
            <div className="modal-actions">
              <button onClick={handleCloseDevelopingModal} className="primary-btn">
                {t('return_home')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 功能更新通知弹窗 */}
      {showUpdateNotice && (
        <div className="report-modal-overlay">
          <div className="update-notice-modal">
            <button className="update-notice-close" onClick={handleDismissUpdate}>×</button>
            <div className="update-notice-header">
              <span className="update-notice-icon">🎉</span>
              <h2>新功能上线</h2>
            </div>
            <div className="update-notice-body">
              <p>形成语感的关键是<strong>重复使用</strong>，也就是系统的复习。但有些表达，我们通常只会在认识它的第一面记得造一下句子。</p>
              <p>而我们的新功能，通过和 AI 的对话<strong>收集有用的表达</strong>，形成在线知识库。</p>
              <p>又将你训练中的每句话和知识库的内容进行匹配，<strong>提醒你频繁使用知识库中更好的表达</strong>，帮助你不断地和一个表达见面，直到成为终身难忘的老友 🤝</p>
            </div>
            <div className="update-notice-actions">
              <button className="update-notice-dismiss" onClick={handleDismissUpdate}>忽略</button>
              <button className="update-notice-go" onClick={handleGoToFreeTalk}>立即体验 →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;